import { useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { unitsApi, type UnitView } from "../api/catalog";
import type { ApiError } from "../api/client";
import { formatApiError } from "../lib/apiError";
import { Modal } from "./Modal";

/**
 * Create / edit form of one unit of measure (sprint 5k): the symbol shown next
 * to a value ("kW"), an optional spelled-out name ("kilovat") and a note. Used
 * by the codebook page; the attribute form adds a unit with a one-field prompt
 * (symbol only) instead, like "Novi proizvođač" on the asset form.
 */
export function UnitFormModal({ unit, onClose, onSaved }: {
  /** The unit being edited; null for a new one. */
  unit: UnitView | null;
  onClose: () => void;
  onSaved: (saved: UnitView, created: boolean) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const editing = unit;
  const [symbol, setSymbol] = useState(editing?.symbol ?? "");
  const [name, setName] = useState(editing?.name ?? "");
  const [note, setNote] = useState(editing?.note ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
  }, [symbol, name, note]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = symbol.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const body = { symbol: trimmed, name: name.trim() || null, note: note.trim() || null };
      const saved = editing ? await unitsApi.update(editing.id, body) : await unitsApi.create(body);
      await onSaved(saved, editing === null);
    } catch (err) {
      const ae = err as ApiError;
      setError(ae.code === "SYMBOL_TAKEN"
        ? t("units.error.symbolTaken", "Jedinica „{{symbol}}“ već postoji.", { symbol: trimmed })
        : formatApiError(ae));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={editing ? t("units.modal.editTitle", "Izmena jedinice mere") : t("units.modal.createTitle", "Nova jedinica mere")}
      onClose={onClose}
      width={420}
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {editing && editing.attributeCount > 0 && (
          <div className="muted" style={{ fontSize: 13 }} role="note">
            {t("units.modal.inUseNote", "U njoj se meri {{count}} atributa — nova oznaka važi za sve njih.", { count: editing.attributeCount })}
          </div>
        )}
        <div className="form-grid">
          <label className="field">
            <span>{t("units.field.symbol", "Oznaka")} <span className="req-mark">*</span></span>
            <input
              className="input"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              required
              maxLength={20}
              placeholder={t("units.field.symbolPlaceholder", "npr. kW")}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
            />
          </label>
          <label className="field">
            <span>{t("units.field.name", "Naziv")}</span>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              placeholder={t("units.field.namePlaceholder", "npr. kilovat")}
            />
          </label>
        </div>
        <label className="field">
          <span>{t("units.field.note", "Napomena")}</span>
          <textarea
            className="input"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={2000}
            placeholder={t("units.field.notePlaceholder", "npr. za šta se koristi, preračun…")}
            style={textarea}
          />
        </label>
        {error && <div style={errorBox} role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <button type="submit" className="btn primary" disabled={submitting || !symbol.trim()} title={!symbol.trim() ? t("units.field.symbolRequired", "Unesite oznaku jedinice.") : undefined}>
            {submitting ? t("action.saving", "Čuvanje…") : t("action.save", "Sačuvaj")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const textarea: CSSProperties = { resize: "vertical", fontFamily: "inherit", width: "100%" };
